import { QuotaManager } from './QuotaManager';
import { ERROR_MESSAGES, QUOTA_CONFIG, GEOCODER_CONFIG } from '../utils/constants';
import { isValidCoordinate } from '../utils/validation';
import {
  AMapGeocoder,
  AMapGeocoderResult,
  AMapInstance,
  AMapMarker,
  Coordinate,
  GeocodingResult,
  GeocodingError,
  QuotaStats,
  GeocodingCandidate
} from '../types';

interface GeocodeResponse {
  success: boolean;
  error?: string;
  code?: string;
  canRetry?: boolean;
  data?: any[];
}

interface BatchProgress {
  current: number;
  total: number;
  success: number;
  errors: number;
  results: GeocodingResult[];
  errorList: GeocodingError[];
  lastResult: GeocodingResult | null;
  lastError: GeocodingError | null;
}

export class MapService {
  private geocoder: AMapGeocoder;
  public quotaManager: QuotaManager;

  constructor(geocoder: AMapGeocoder) {
    this.geocoder = geocoder;
    this.quotaManager = new QuotaManager();
  }

  normalizeLocation(location: any): Coordinate | null {
    if (!location) {
      return null;
    }

    try {
      if (typeof location.getLng === 'function' && typeof location.getLat === 'function') {
        const lng = Number(location.getLng());
        const lat = Number(location.getLat());
        if (Number.isFinite(lng) && Number.isFinite(lat)) {
          return { lng, lat };
        }
        return null;
      }

      if (typeof location.lng !== 'undefined' && typeof location.lat !== 'undefined') {
        const lng = Number(location.lng);
        const lat = Number(location.lat);
        if (Number.isFinite(lng) && Number.isFinite(lat)) {
          return { lng, lat };
        }
        return null;
      }

      if (typeof location === 'string') {
        const [lngStr, latStr] = location.split(',');
        const lng = Number(lngStr);
        const lat = Number(latStr);
        if (Number.isFinite(lng) && Number.isFinite(lat)) {
          return { lng, lat };
        }
        return null;
      }
    } catch (error) {
      console.warn('规范化坐标失败:', error, location);
      return null;
    }

    return null;
  }

  async geocodeAddress(address: string, retryCount: number = 0): Promise<GeocodeResponse> {
    if (!this.geocoder) {
      throw new Error('地理编码器未初始化');
    }

    await this.quotaManager.waitForSlot();

    return new Promise<GeocodeResponse>((resolve) => {
      let settled = false;
      let timeoutId: NodeJS.Timeout | undefined;

      const finish = (payload: GeocodeResponse) => {
        if (settled) return;
        settled = true;
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        resolve(payload);
      };

      timeoutId = setTimeout(() => {
        finish({
          success: false,
          error: '请求超时，请稍后重试',
          code: 'TIMEOUT',
          canRetry: true
        });
      }, GEOCODER_CONFIG.TIMEOUT_MS);

      this.geocoder.getLocation(address, (status: string, result: AMapGeocoderResult) => {
        if (settled) {
          return;
        }

        if (status === 'complete' && result.geocodes && result.geocodes.length > 0) {
          this.quotaManager.handleSuccess();
          finish({
            success: true,
            data: result.geocodes
          });
        } else if ((result as any).infocode === '10021') {
          this.quotaManager.handleQuotaError();

          if (retryCount < QUOTA_CONFIG.MAX_RETRIES) {
            console.log(`第 ${retryCount + 1} 次重试地址: ${address}`);
            if (timeoutId) {
              clearTimeout(timeoutId);
              timeoutId = undefined;
            }
            setTimeout(() => {
              this.geocodeAddress(address, retryCount + 1).then(finish);
            }, QUOTA_CONFIG.RETRY_DELAY);
            return;
          }

          finish({
            success: false,
            error: '请求频率超限，请稍后重试',
            code: '10021',
            canRetry: true
          });
        } else {
          const infocode = (result as any)?.infocode;
          const errorMessage = infocode && ERROR_MESSAGES[infocode]
            ? ERROR_MESSAGES[infocode]
            : '地址解析失败';
          const canRetry = infocode === '20001' ? false : true;

          finish({
            success: false,
            error: errorMessage,
            code: infocode || 'UNKNOWN',
            canRetry
          });
        }
      });
    });
  }

  async batchGeocode(
    addresses: string[],
    prefix: string = '',
    onProgress: ((progress: BatchProgress) => void) | null = null
  ): Promise<{ results: GeocodingResult[]; errors: GeocodingError[] }> {
    const results: GeocodingResult[] = [];
    const errors: GeocodingError[] = [];

    for (let i = 0; i < addresses.length; i++) {
      const address = addresses[i].trim();
      if (!address) continue;

      const fullAddress = prefix ? `${prefix}${address}` : address;

      try {
        const result = await this.geocodeAddress(fullAddress);

        if (result.success && result.data) {
          const geocode = result.data[0];
          const normalizedLocation = this.normalizeLocation(geocode.location);
          const candidates: GeocodingCandidate[] = result.data.length > 1
            ? result.data.slice(1).map(candidate => ({
              formatted_address: candidate.formatted_address,
              location: this.normalizeLocation(candidate.location),
              addressComponent: candidate.addressComponent
            }))
            : [];

          results.push({
            originalAddress: address,
            fullAddress,
            location: normalizedLocation,
            formattedAddress: geocode.formatted_address,
            addressComponent: geocode.addressComponent,
            candidates,
            order: i
          });
        } else {
          errors.push({
            address: fullAddress,
            originalAddress: address,
            error: result.error || '未知错误',
            code: result.code,
            canRetry: result.canRetry !== false,
            retrying: false,
            order: i
          });
        }
      } catch (error) {
        errors.push({
          address: fullAddress,
          originalAddress: address,
          error: (error as Error).message,
          code: 'EXCEPTION',
          canRetry: true,
          retrying: false,
          order: i
        });
      }

      if (onProgress) {
        onProgress({
          current: i + 1,
          total: addresses.length,
          success: results.length,
          errors: errors.length,
          results: results.slice(),
          errorList: errors.slice(),
          lastResult: results[results.length - 1] || null,
          lastError: errors[errors.length - 1] || null
        });
      }
    }

    return { results, errors };
  }

  addMarkersToMap(
    map: AMapInstance,
    results: GeocodingResult[],
    onMarkerClick: ((result: GeocodingResult, index: number) => void) | null = null
  ): (AMapMarker | undefined)[] {
    if (!map) return [];

    const markers = results.map((result, index) => {
      // 验证坐标有效性
      if (!result.location ||
          typeof result.location.lng !== 'number' ||
          typeof result.location.lat !== 'number' ||
          isNaN(result.location.lng) ||
          isNaN(result.location.lat)) {
        console.warn(`跳过无效坐标的地址: ${result.formattedAddress}`, result.location);
        return undefined;
      }

      const marker = new (window as any).AMap.Marker({
        position: [result.location.lng, result.location.lat],
        title: result.formattedAddress,
        label: {
          content: `${index + 1}`,
          offset: new (window as any).AMap.Pixel(0, 0),
          direction: 'center'
        }
      }) as AMapMarker;

      if (onMarkerClick) {
        marker.on('click', () => onMarkerClick(result, index));
      }

      map.add(marker);
      return marker;
    });

    if (results.length > 0) {
      const bounds = new (window as any).AMap.Bounds();
      results.forEach(result => {
        // 验证坐标有效性
        if (isValidCoordinate(result.location)) {
          bounds.extend([result.location.lng, result.location.lat]);
        }
      });
      map.setBounds(bounds, false, [20, 20, 20, 20]);
    }

    return markers;
  }

  clearMarkers(map: AMapInstance, markers: (AMapMarker | undefined)[]): void {
    if (map && markers && Array.isArray(markers)) {
      // 过滤掉 undefined/null 的标记，只移除有效的标记
      const validMarkers = markers.filter(
        (marker): marker is AMapMarker =>
          marker !== undefined && marker !== null && typeof (marker as any).getPosition === 'function'
      );
      if (validMarkers.length > 0) {
        map.remove(validMarkers);
      }
    }
  }

  highlightMarker(markers: (AMapMarker | undefined)[], index: number, highlight: boolean = true): void {
    const marker = markers[index];
    if (marker && typeof (marker as any).setIcon === 'function') {
      try {
        if (highlight) {
          (marker as any).setIcon('//a.amap.com/jsapi_demos/static/demo-center/icons/poi-marker-red.png');
          (marker as any).setZIndex(1000);
        } else {
          (marker as any).setIcon(null);
          (marker as any).setZIndex(100);
        }
      } catch (error) {
        console.warn('设置标记样式失败:', error);
      }
    }
  }

  getQuotaStats(): QuotaStats {
    return this.quotaManager.getStats();
  }

  resetQuota(): void {
    this.quotaManager.reset();
  }
}
